import React, { lazy } from 'react';
import { ExternalLinkIcon, BookOpenIcon } from 'lucide-react';
import { PageTransition } from '../components/layout/PageTransition';
import { DecorativeShape } from '../components/DecorativeShape';
import { Card } from '../components/ui/Card';
import { Animate } from '../components/ui/Animate';
import { useLanguage } from '../context/LanguageContext';
interface Credential {
  code: string;
  label: string;
}
interface TimelineEvent {
  year: string;
  title: string;
  desc: string;
}
interface WritingItem {
  title: string;
  description: string;
  tag: string;
  url: string;
}
const credentialsEN: Credential[] = [{
  code: 'ICF ACC',
  label: 'Certified Coach'
}, {
  code: 'ICP-LEA',
  label: 'Agile Leadership'
}, {
  code: 'ICP-AHR',
  label: 'Agile HR'
}, {
  code: 'ICP-BAF',
  label: 'Business Agility'
}, {
  code: 'LeSS',
  label: 'LeSS Practitioner'
}, {
  code: 'RMP',
  label: 'Risk Management'
}];
const credentialsIS: Credential[] = [{
  code: 'ICF ACC',
  label: 'Vottaður Markþjálfi'
}, {
  code: 'ICP-LEA',
  label: 'Agile Leiðtogafærni'
}, {
  code: 'ICP-AHR',
  label: 'Agile HR'
}, {
  code: 'ICP-BAF',
  label: 'Viðskiptasveigjanleiki'
}, {
  code: 'LeSS',
  label: 'LeSS Practitioner'
}, {
  code: 'RMP',
  label: 'Reiss Motivation Profile'
}];
const writingEN: WritingItem[] = [{
  title: 'Developing Highly Effective Teams',
  description: 'What Tuckman, Wheelan, Lencioni and Helfand actually say about how teams form - and why most organisations get it exactly backwards.',
  tag: 'Teams',
  url: 'https://vidskiptaleikni.substack.com/p/developing-highly-effective-teams'
}, {
  title: 'Coaching Leaders and Team Coaches',
  description: 'Why managers and agile coaches can never be professional coaches for their own people - and what they should do instead.',
  tag: 'Leadership',
  url: 'https://helgig.substack.com/p/markjalfandi-stjornendur-og-teymisjalfar'
}, {
  title: 'Give and Receive Positive Feedback',
  description: 'The paradox of feedback, why "constructive" feedback is neurologically counterproductive, and what the Gallup research actually shows.',
  tag: 'Feedback',
  url: 'https://www.agilepeoplemanifesto.org'
}, {
  title: 'Emergent Strategy',
  description: 'Why traditional strategic planning has stopped working in complex environments - and what organisations need to do instead.',
  tag: 'Strategy',
  url: 'https://www.agilepeoplemanifesto.org'
}];
const writingIS: WritingItem[] = [{
  title: 'Að þróa árangursrík teymi',
  description: 'Hvað Tuckman, Wheelan, Lencioni og Helfand segja í raun um hvernig teymi myndast - og hvers vegna flest fyrirtæki gera það nákvæmlega öfugt.',
  tag: 'Teymi',
  url: 'https://vidskiptaleikni.substack.com/p/developing-highly-effective-teams'
}, {
  title: 'Markþjálfandi stjórnendur og teymisþjálfar',
  description: 'Hvers vegna stjórnendur og agile þjálfar geta aldrei verið faglegir markþjálfar fyrir eigið fólk - og hvað þeir ættu að gera í staðinn.',
  tag: 'Leiðtogi',
  url: 'https://helgig.substack.com/p/markjalfandi-stjornendur-og-teymisjalfar'
}, {
  title: 'Að veita og þiggja jákvæða endurgjöf',
  description: 'Þversögnin í endurgjöf, hvers vegna „uppbyggileg" endurgjöf er taugafræðilega gagnslaus - og hvað rannsóknir Gallup sýna í raun.',
  tag: 'Endurgjöf',
  url: 'https://www.agilepeoplemanifesto.org'
}, {
  title: 'Emergent Strategy',
  description: 'Hvers vegna hefðbundin stefnumörkun hefur hætt að virka í flóknum umhverfi - og hvað fyrirtæki þurfa að gera í staðinn.',
  tag: 'Stefna',
  url: 'https://www.agilepeoplemanifesto.org'
}];
const tagColors: Record<string, string> = {
  Teams: 'bg-teal/10 text-teal-700',
  Teymi: 'bg-teal/10 text-teal-700',
  Leadership: 'bg-coral/10 text-coral-700',
  Leiðtogi: 'bg-coral/10 text-coral-700',
  Feedback: 'bg-yellow-100 text-yellow-700',
  Endurgjöf: 'bg-yellow-100 text-yellow-700',
  Strategy: 'bg-purple-100 text-purple-700',
  Stefna: 'bg-purple-100 text-purple-700'
};
export function About() {
  const {
    t,
    language
  } = useLanguage();
  const timelineEvents = t('about.timeline') as TimelineEvent[];
  const credentials = language === 'is' ? credentialsIS : credentialsEN;
  const writing = language === 'is' ? writingIS : writingEN;
  const publication = t('about.publication') as {
    title: string;
    subtitle: string;
    chapters: string;
  };
  return <PageTransition data-id="element-143">
      <div className="pt-12 pb-24 overflow-hidden" data-id="element-144">
        {/* Header */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-145">
          <Animate className="text-center max-w-3xl mx-auto relative" data-id="element-146">
            <DecorativeShape type="circle" color="bg-yellow-200" className="top-0 left-1/2 w-32 h-32 -translate-x-1/2 -translate-y-1/2 -z-10 blur-xl opacity-50" data-id="element-147" />
            <h1 className="text-5xl md:text-6xl font-bold mb-6" data-id="element-148">
              {t('about.title')}
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed" data-id="element-149">
              {t('about.intro')}
            </p>
          </Animate>
        </section>

        {/* Story & Photo Grid */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-24" data-id="element-150">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start" data-id="element-151">
            <div className="grid grid-cols-2 gap-4" data-id="element-152">
              <Animate className="space-y-4 mt-8" data-id="element-153">
                <img src="https://cdn.magicpatterns.com/uploads/6bt3PcPytwUVW4EyeZ9z5f/Helgi-6_A_height_x30_copy.jpg" alt="Helgi Gudmundsson" className="rounded-3xl shadow-lg w-full h-72 object-cover object-top" loading="lazy" data-id="element-154" />
                <div className="bg-coral p-6 rounded-3xl text-white flex items-center justify-center h-40" data-id="element-155">
                  <p className="font-heading font-bold text-xl text-center" data-id="element-156">
                    {t('about.pill1')}
                  </p>
                </div>
              </Animate>
              <Animate delay={0.2} className="space-y-4" data-id="element-157">
                <div className="bg-teal p-6 rounded-3xl text-white flex items-center justify-center h-40" data-id="element-158">
                  <p className="font-heading font-bold text-xl text-center" data-id="element-159">
                    {t('about.pill2')}
                  </p>
                </div>
                <img src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80" alt="Workshop facilitation" className="rounded-3xl shadow-lg w-full h-72 object-cover" loading="lazy" data-id="element-160" />
              </Animate>
            </div>

            <Animate delay={0.1} className="space-y-6 text-gray-600 text-lg leading-relaxed" data-id="element-161">
              <h2 className="text-3xl font-bold text-charcoal" data-id="element-162">
                {t('about.storyTitle')}
              </h2>
              <p data-id="element-163">{t('about.storyP1')}</p>
              <p data-id="element-164">{t('about.storyP2')}</p>
              <p data-id="element-165">{t('about.storyP3')}</p>

              {/* Publication callout */}
              <div className="border-l-4 border-teal pl-5 py-1 mt-6" data-id="element-166">
                <p className="text-sm font-bold text-teal uppercase tracking-wider mb-1" data-id="element-167">
                  {t('about.publicationLabel')}
                </p>
                <p className="font-bold text-charcoal text-base" data-id="element-168">
                  {publication.title}
                </p>
                <p className="text-sm text-gray-500" data-id="element-169">{publication.subtitle}</p>
                <p className="text-sm text-gray-500 italic mt-1" data-id="element-170">
                  {publication.chapters}
                </p>
              </div>
            </Animate>
          </div>
        </section>

        {/* Credentials bar */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-24" data-id="element-171">
          <Animate data-id="element-172">
            <h2 className="text-lg font-bold text-gray-400 uppercase tracking-wider text-center mb-8" data-id="element-173">
              {t('about.credentialsTitle')}
            </h2>
            <div className="flex flex-wrap justify-center gap-3" data-id="element-174">
              {credentials.map(c => <div key={c.code} className="flex flex-col items-center bg-white border border-gray-100 rounded-2xl px-5 py-3 shadow-sm" data-id="element-175">
                  <span className="font-bold text-charcoal text-sm" data-id="element-176">
                    {c.code}
                  </span>
                  <span className="text-gray-400 text-xs mt-0.5" data-id="element-177">
                    {c.label}
                  </span>
                </div>)}
            </div>
          </Animate>
        </section>

        {/* Timeline */}
        <section className="bg-cream py-24 relative" data-id="element-178">
          <DecorativeShape type="blob" color="bg-teal/10" className="bottom-0 right-0 w-96 h-96 translate-y-1/3 translate-x-1/3" data-id="element-179" />
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-180">
            <h2 className="text-3xl font-bold text-center mb-16" data-id="element-181">
              {t('about.timelineTitle')}
            </h2>
            <div className="relative" data-id="element-182">
              <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gray-200 rounded-full hidden md:block" data-id="element-183" />
              <div className="space-y-12" data-id="element-184">
                {timelineEvents.map((event, index) => <Animate key={index} direction={index % 2 === 0 ? 'right' : 'left'} delay={index * 0.1} className={`flex flex-col md:flex-row items-center gap-8 ${index % 2 === 0 ? 'md:flex-row-reverse' : ''}`} data-id="element-185">
                    <div className="flex-1 w-full md:w-auto" data-id="element-186">
                      <Card className="p-6 relative" data-id="element-187">
                        <span className="absolute -top-4 -right-4 bg-coral text-white font-bold px-3 py-1 rounded-full text-sm shadow-md" data-id="element-188">
                          {event.year}
                        </span>
                        <h3 className="text-xl font-bold mb-2 text-teal" data-id="element-189">
                          {event.title}
                        </h3>
                        <p className="text-gray-600" data-id="element-190">{event.desc}</p>
                      </Card>
                    </div>
                    <div className="relative z-10 flex-shrink-0" data-id="element-191">
                      <div className="w-4 h-4 bg-coral rounded-full ring-4 ring-white shadow-md hidden md:block" data-id="element-192" />
                    </div>
                    <div className="flex-1 hidden md:block" data-id="element-193" />
                  </Animate>)}
              </div>
            </div>
          </div>
        </section>

        {/* Writing section */}
        <section className="py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-194">
          <Animate className="mb-12" data-id="element-195">
            <div className="flex items-center gap-3 mb-3" data-id="element-196">
              <BookOpenIcon size={20} className="text-teal" data-id="element-197" />
              <span className="text-teal font-bold tracking-wider uppercase text-sm" data-id="element-198">
                {'Skrif og \xFEankar'}
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold" data-id="element-199">
              {t('about.writingTitle')}
            </h2>
            <p className="text-gray-500 mt-3 text-lg max-w-2xl" data-id="element-200">
              {t('about.writingSubtitle')}
            </p>
          </Animate>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-201">
            {writing.map((item, i) => <Animate key={i} delay={i * 0.1} data-id="element-202">
                <a href={item.url} target="_blank" rel="noopener noreferrer" className="block h-full" data-id="element-203">
                  <Card hoverEffect className="h-full p-6 group border-2 border-transparent hover:border-teal/20 transition-colors" data-id="element-204">
                    <div className="flex items-start justify-between gap-4 mb-3" data-id="element-205">
                      <span className={`text-xs font-bold px-3 py-1 rounded-full ${tagColors[item.tag] ?? 'bg-gray-100 text-gray-600'}`} data-id="element-206">
                        {item.tag}
                      </span>
                      <ExternalLinkIcon size={16} className="text-gray-300 group-hover:text-teal transition-colors flex-shrink-0 mt-0.5" data-id="element-207" />
                    </div>
                    <h3 className="font-bold text-lg text-charcoal mb-2 group-hover:text-teal transition-colors" data-id="element-208">
                      {item.title}
                    </h3>
                    <p className="text-gray-500 text-sm leading-relaxed" data-id="element-209">
                      {item.description}
                    </p>
                  </Card>
                </a>
              </Animate>)}
          </div>
        </section>

        {/* Outside work note */}
        <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 text-center" data-id="element-210">
          <Animate data-id="element-211">
            <p className="text-gray-400 text-lg leading-relaxed italic" data-id="element-212">
              {t('about.outsideWork')}
            </p>
          </Animate>
        </section>
      </div>
    </PageTransition>;
}